import { INITIAL_STATE, LS_KEY, LS_VERSION, MAX_LS_ITEMS, XHR_URL, CLASSNAMES } from './constants'
import Item from './item'
import { rapidReady } from '../../../../lib/utils/rapid'

export default (() =>
  class RecentlyViewed {
    constructor() {
      this.presistantState = this._getLocalStorage()
      this._addPublicInterface()

      this.elem = document.getElementsByClassName(CLASSNAMES.RV)[0]
      if (this.elem) {
        this.init()
      }
    }

    async init() {
      const items = this.getItems()
      if (items.length <= 0) return

      try {
        const rvInner = this.elem.getElementsByClassName(CLASSNAMES.RV_INNER)[0]
        const recentlyViewedMarkup = await this._fetchMarkup(items)
        if (rvInner) {
          // eslint-disable-next-line no-unsanitized/property
          rvInner.innerHTML = recentlyViewedMarkup
          this.syncUI()
        }
      } catch (e) {
        console.error(e)
      }
    }

    syncUI() {
      wafer.base.sync(this.elem)
      this._refreshRapid(this.elem)
    }

    getItems() {
      return this.presistantState.items
    }

    add(obj) {
      const item = new Item(obj)
      const { items } = this.presistantState
      const existingItemIdx = items.findIndex(i => i.id === item.id && i.type === item.type)
      const existingItem = items[existingItemIdx]
      // If the item is already in the list, replace the existing obj, and move to the end of the list
      if (existingItem) {
        items.splice(existingItemIdx, 1)
        items.push(item)
      }
      // If the list is full, remove the first item and add the new one
      else if (items.length >= MAX_LS_ITEMS) {
        items.shift()
        items.push(item)
      }
      // Push item to the list
      else {
        items.push(item)
      }

      this._syncLocalStorage()
    }

    // PRIVATE METHODS
    _addPublicInterface() {
      window.AOL = window.AOL || {}
      window.AOL._recentlyViewed = window.AOL._recentlyViewed || []

      while (window.AOL._recentlyViewed.length > 0) {
        const recentlyViewed = window.AOL._recentlyViewed.shift()
        try {
          this.add(recentlyViewed)
        } catch (e) {
          // Do nothing as item was invalid
        }
      }

      window.AOL.recentlyViewed = {
        getItems: this.getItems.bind(this),
        add: this.add.bind(this)
      }
    }

    _getLocalStorage() {
      const lsString = window.localStorage.getItem(LS_KEY)
      let lsObj = structuredClone(INITIAL_STATE)

      if (!lsString) {
        return lsObj
      }

      try {
        const json = window.atob(lsString)
        lsObj = JSON.parse(json, this._localStorageDeserializer.bind(this))

        const isValid = this._validate(lsObj)
        if (!isValid) {
          // LS data is invalid remove and reset
          window.localStorage.removeItem(LS_KEY)
          lsObj = structuredClone(INITIAL_STATE)
        }
      } catch (e) {
        // failed to read LS, clean up the corrupt memory
        window.localStorage.removeItem(LS_KEY)
      }

      return lsObj
    }

    _validate(obj) {
      let isValid = true
      if (
        obj.version !== LS_VERSION ||
        !Array.isArray(obj.items) ||
        obj.items.length > MAX_LS_ITEMS
      ) {
        return false
      }
      obj.items.forEach(item => {
        if (!(item instanceof Item)) {
          isValid = false
        }
      })

      return isValid
    }

    _syncLocalStorage() {
      const serialized = {
        ...this.presistantState,
        items: this.presistantState.items.map(item => item.serialize())
      }
      const stringified = JSON.stringify(serialized)
      // base64 encode is only there to obfuscate the data
      const base64Encoded = window.btoa(stringified)
      window.localStorage.setItem(LS_KEY, base64Encoded)
    }

    _localStorageDeserializer(key, value) {
      if (key === 'items') {
        return value.map(item => {
          const newItem = new Item()
          newItem.deserialize(item)
          return newItem
        })
      }
      return value
    }

    async _fetchMarkup(items) {
      let html = ''
      try {
        const serializedItems = items.map(item => item.serialize())
        const response = await fetch(XHR_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: serializedItems })
        })
        const data = await response.json()
        ;({ html } = data)
      } catch (e) {
        console.error(e)
      }
      return html
    }

    _refreshRapid(elem = {}) {
      const { id } = elem
      if (!id) return
      rapidReady(rapid => {
        if (rapid.isModuleTracked(id)) {
          rapid.refreshModule(id)
        } else {
          rapid.addModulesWithViewability([id])
        }
      })
    }
  })()
